import React, { useEffect, useState } from "react";
import {
  useNavigate,
  useOutletContext,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import GlobalLoader from "../../common/GlobalLoader";
import CommonTable from "../../common/commonTable";
import OverallTestAttemptViewer from "./OverallTestAttemptViewer";
import EmptyPage from "../../common/emptyPage";
import {
  getOverallTests,
  deleteOverallTest,
  getOverallTestSubmittedStudents,
  getOverallTestSubmittedStudent,
} from "../../../helper/apicalls/overallTest";
import "../../../styles/admin/overall.scss";
import { AddIcon, ArrowBackIcon } from "../../../icons";
import testIcon from "../../../assets/png/testIcon.png";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import GenerateOverallTestPopup from "../../common/GenerateOverallTestPopup";
import DeleteConfirmPopUp from "../../common/deleteConfirmPopUp";
import Cookies from "js-cookie";

const OverallTestList = () => {
  const { setHeaderName } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();
  const role = Cookies.get("role");
  const portalBase =
    role === "admin"
      ? "/admin"
      : role === "teacher"
        ? "/teacher"
        : "/institution";
  const [searchParams] = useSearchParams();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [showGeneratePopup, setShowGeneratePopup] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [selectedTestForDelete, setSelectedTestForDelete] = useState(null);
  const [showSubmissionsPopup, setShowSubmissionsPopup] = useState(false);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [submissionsError, setSubmissionsError] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [selectedTestForSubmissions, setSelectedTestForSubmissions] =
    useState(null);
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  const [attemptLoading, setAttemptLoading] = useState(false);
  const [attemptError, setAttemptError] = useState(null);

  const fetchTests = async () => {
    try {
      setLoading(true);
      setError(null);
      const courseKey = searchParams.get("course") || "";
      const data = await getOverallTests(courseKey);
      setTests(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch overall tests:", err);
      setError("Unable to load overall tests. Please try again.");
      setTests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBackFromSubmissions = () => {
    if (selectedAttempt || attemptLoading || attemptError) {
      setSelectedAttempt(null);
      setAttemptError(null);
      setAttemptLoading(false);
      return;
    }

    setShowSubmissionsPopup(false);
    setSelectedTestForSubmissions(null);
    setSubmissions([]);
    setSubmissionsError(null);
  };

  const handleSubmissionRowClick = async (row) => {
    try {
      const submissionId = row?.id || row?._id || row?.studentId;
      if (!submissionId) return;
      setAttemptLoading(true);
      setAttemptError(null);
      const details = await getOverallTestSubmittedStudent(submissionId);
      setSelectedAttempt(details || null);
    } catch (err) {
      setAttemptError(
        "Unable to load attempt details for this submission. Please try again.",
      );
    }
    setAttemptLoading(false);
  };

  const toggleMenu = (e, index) => {
    e.stopPropagation();
    setOpenMenuIndex((prev) => (prev === index ? null : index));
  };

  const closeMenu = () => setOpenMenuIndex(null);

  useEffect(() => {
    if (openMenuIndex === null) return;

    const handleDocumentClick = (event) => {
      const target = event.target;
      if (target.closest && target.closest(".overall-tests-menu")) {
        return;
      }
      setOpenMenuIndex(null);
    };

    document.addEventListener("click", handleDocumentClick);

    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  }, [openMenuIndex]);

  const isDeadlinePassed = (test) => {
    if (!test?.deadline) return false;
    const deadlineDate = new Date(test.deadline);
    if (Number.isNaN(deadlineDate.getTime())) return false;
    return deadlineDate.getTime() < Date.now();
  };

  const openSubmissionsPopup = async (test) => {
    const id = test?.id;
    if (!id) return;
    setSelectedTestForSubmissions(test);
    setShowSubmissionsPopup(true);
    setSubmissionsLoading(true);
    setSubmissionsError(null);
    try {
      const data = await getOverallTestSubmittedStudents(id);
      const raw = Array.isArray(data) ? data : [];
      const normalized = raw.map((item, idx) => {
        const u = item && item.user ? item.user : {};
        return {
          id: item?.id || item?._id || u.id || idx,
          created_at: item?.created_at || "",
          no_of_question_correct: item?.no_of_question_correct ?? "",
          no_of_questions_attempted: item?.no_of_questions_attempted ?? "",
          test: item?.test || "",
          updated_at: item?.updated_at || "",
          name: u.name || "",
          email: u.email || "",
          register_no: u.register_no || "",
          grade: u.grade || "",
          batch_name: u.batch_name || "",
        };
      });
      setSubmissions(normalized);
      setSelectedAttempt(null);
      setAttemptError(null);
    } catch (err) {
      console.error("Failed to fetch submitted students:", err);
      setSubmissionsError(
        "Unable to load submitted students. Please try again later.",
      );
      setSubmissions([]);
    } finally {
      setSubmissionsLoading(false);
    }
  };

  const handleTestClick = (test) => {
    if (isDeadlinePassed(test)) {
      openSubmissionsPopup(test);
      return;
    }
    const id = test?.id;
    if (!id) return;
    navigate(`${portalBase}/overall/view?id=${encodeURIComponent(id)}`);
  };

  const handleEditClick = (test) => {
    const id = test?.id;
    if (!id) return;
    setOpenMenuIndex(null);
    navigate(
      `${portalBase}/overall/view?id=${encodeURIComponent(id)}&mode=edit`,
    );
  };

  const handleRequestDelete = (test) => {
    setSelectedTestForDelete(test || null);
    setConfirmDeleteOpen(true);
    setOpenMenuIndex(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedTestForDelete?.id) {
      setConfirmDeleteOpen(false);
      return;
    }
    try {
      await deleteOverallTest(selectedTestForDelete.id);
      setConfirmDeleteOpen(false);
      setSelectedTestForDelete(null);
      fetchTests();
    } catch (err) {
      console.error("Failed to delete overall test:", err);
      setConfirmDeleteOpen(false);
    }
  };

  useEffect(() => {
    setHeaderName("Tests");
    fetchTests();
  }, [setHeaderName, searchParams]);

  const submissionColumns = [
    {
      field: "name",
      label: "Name",
    },
    {
      field: "email",
      label: "Email",
    },
    {
      field: "register_no",
      label: "Register No",
    },
    {
      field: "grade",
      label: "Grade",
    },
    {
      field: "batch_name",
      label: "Batch",
    },
    {
      field: "no_of_question_correct",
      label: "No. of Questions Correct",
    },
    {
      field: "no_of_questions_attempted",
      label: "No. of Questions Attempted",
    },
    {
      field: "created_at",
      label: "Submitted At",
    },
  ];

  return (
    <div className="overall-tests-container">
      {loading && (
        <GlobalLoader visible={loading} text="Loading overall tests..." />
      )}
      <div className="overall-tests-card">
        <div className="overall-tests-header">
          <span className="overall-tests-title">
            {showSubmissionsPopup ? (
              <>
                <ArrowBackIcon
                  className="overall-tests-back-icon-btn"
                  onClick={handleBackFromSubmissions}
                />
                <span className="overall-tests-title-text">
                  {selectedTestForSubmissions?.name ||
                    selectedTestForSubmissions?.title ||
                    "Submitted Students"}
                </span>
              </>
            ) : (
              "Overall Tests"
            )}
          </span>
          <div className="overall-tests-actions">
            {!showSubmissionsPopup && (
              <button
                type="button"
                className="overall-tests-add-btn"
                onClick={() => setShowGeneratePopup(true)}
              >
                <AddIcon />
                <span>Add Overall Test</span>
              </button>
            )}
          </div>
        </div>
        {showSubmissionsPopup ? (
          <div className="overall-tests-submissions-panel">
            <div className="overall-tests-submissions-body">
              {submissionsLoading && (
                <GlobalLoader
                  visible={submissionsLoading}
                  text="Loading submitted students..."
                />
              )}
              {submissionsError && (
                <div className="overall-tests-error">{submissionsError}</div>
              )}
              {!submissionsLoading && !submissionsError && (
                <>
                  {selectedAttempt || attemptLoading || attemptError ? (
                    <div className="overall-tests-submissions-attempt">
                      {attemptLoading && (
                        <GlobalLoader
                          visible={attemptLoading}
                          text="Loading attempt details..."
                        />
                      )}
                      {attemptError && !attemptLoading && (
                        <div className="overall-tests-error">
                          {attemptError}
                        </div>
                      )}
                      {!attemptLoading && !attemptError && selectedAttempt && (
                        <OverallTestAttemptViewer
                          attemptData={selectedAttempt.attempt_data || []}
                        />
                      )}
                    </div>
                  ) : (
                    <div className="overall-tests-submissions-table">
                      <CommonTable
                        data={submissions}
                        columns={submissionColumns}
                        getRowId={(row, idx) =>
                          row.id || row._id || row.studentId || idx
                        }
                        title="Submitted Students"
                        showActions={false}
                        maxBodyHeight="60vh"
                        onRowClick={handleSubmissionRowClick}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="overall-tests-grid">
              {!loading &&
                !error &&
                tests?.map((test, index) => (
                  <div
                    key={test.id || test.key || test.name}
                    className="overall-tests-item"
                    onClick={() => handleTestClick(test)}
                  >
                    <div className="overall-tests-menu">
                      <button
                        type="button"
                        className="overall-tests-menu-button"
                        onClick={(e) => toggleMenu(e, index)}
                      >
                        <span
                          className={`overall-tests-status-dot ${
                            isDeadlinePassed(test)
                              ? "overall-tests-status-dot--expired"
                              : "overall-tests-status-dot--active"
                          }`}
                        />
                        <MoreVertIcon className="overall-tests-menu-icon" />
                      </button>
                      {openMenuIndex === index && (
                        <div
                          className="overall-tests-menu-list"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            className="overall-tests-menu-item"
                            onClick={() => handleEditClick(test)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="overall-tests-menu-item overall-tests-menu-item--danger"
                            onClick={() => handleRequestDelete(test)}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="overall-tests-icon">
                      <img
                        src={testIcon}
                        alt="testIcon"
                        style={{ width: "75px", height: "75px" }}
                      />
                    </div>
                    <div className="overall-tests-name">
                      {test.name || test.title || "Untitled Test"}
                    </div>
                  </div>
                ))}
            </div>

            {error && <EmptyPage content={error} />}

            {!loading && !error && tests?.length === 0 && (
              <EmptyPage content={"No overall tests found."} />
            )}
          </>
        )}

        {showGeneratePopup && (
          <GenerateOverallTestPopup
            open={showGeneratePopup}
            handleClose={() => setShowGeneratePopup(false)}
            courseKey={(searchParams.get("course") || "").trim()}
            onCreated={(created) => {
              fetchTests();
              setShowGeneratePopup(false);
              const newId = created?.id || created?._id || created?.key;
              if (newId) {
                navigate(
                  `${portalBase}/overall/view?id=${encodeURIComponent(newId)}&mode=edit`,
                );
              }
            }}
          />
        )}

        <DeleteConfirmPopUp
          isConfirmDeleteOpen={confirmDeleteOpen}
          setIsConfirmDeleteOpen={setConfirmDeleteOpen}
          handleDelete={handleConfirmDelete}
          title="Delete Overall Test"
          message="Are you sure you want to delete this overall test? This action cannot be undone."
          confirmLabel="Delete"
          cancelLabel="Cancel"
        />
      </div>
    </div>
  );
};
export default OverallTestList;
